import {
  Button,
  H5,
  H6,
  InputDate,
  InputCheckbox,
  InputNumber,
} from "@sellar-io/components";
import { ProductCreateRequestTypeProductItemType } from "@sellar-io/api-v2/types";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../components/modal/Dialog";
import { Alert } from "../../../../components/alert/Alert";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { useProductUpdate } from "../../../../hooks/api/v2/product/useProductUpdate";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../../components/radioSelector/RadioGroup";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Switch } from "@sellar-io/components";
import dayjs from "dayjs";
import { Icon } from "../../../../components";
import { StockStatusCard } from "../components/StockStatusCard";
import { calculateAvailableStockFromFormInput } from "../helpers/calculateAvailableStock";
import { PreorderMarketingCard } from "../components/PreorderMarketingCard";

interface Props extends Pick<
  ProductCreateRequestTypeProductItemType,
  | "stock"
  | "maxOrderQuantity"
  | "canBackorder"
  | "incomingStock"
  | "availableFrom"
> {
  committedStock: number;
}

type ProductFormInputType = Required<Props>;

const IncomingToOnHandStockTransferConfirmation = ({
  confirmed,
  onConfirmedChanged,
}: {
  confirmed: boolean;
  onConfirmedChanged: (newState: boolean) => void;
}) => {
  return (
    <div className="flex gap-2 rounded-lg border border-zinc-300 bg-zinc-100 p-4">
      <div className="flex shrink-0 items-center">
        <InputCheckbox
          required={false}
          checked={confirmed}
          onChange={(e) => onConfirmedChanged(e.target.checked)}
        />
      </div>

      <div>
        <p>I'm ready to move this stock to general release</p>
      </div>
    </div>
  );
};

const NewTag = () => {
  return (
    <div className="shrink-0 rounded-xl border border-indigo-500 bg-indigo-50 px-2 py-1 text-black text-zinc-800">
      New
    </div>
  );
};

const GeneralReleaseInputForm = ({
  existingData,
  loading,
  onFormSubmit,
}: {
  existingData: ProductFormInputType;
  loading: boolean;
  onFormSubmit: (data: ProductFormInputType) => void;
}) => {
  const [confirmed, setConfirmed] = useState(false);

  const shouldTransferIncomingStockToOnHand = !!existingData.incomingStock;

  const remoteFeatureFlag = useRemoteFeatureFlags();

  const { register, handleSubmit, control } = useForm<ProductFormInputType>({
    defaultValues: {
      incomingStock: 0,
      stock: existingData.stock + existingData.incomingStock,
      maxOrderQuantity: existingData.maxOrderQuantity,
      canBackorder: existingData.canBackorder,
      availableFrom: null,
    },
    shouldUseNativeValidation: true,
  });

  const canBackorder = useWatch({
    control,
    name: "canBackorder",
  });
  const stock = useWatch({
    control,
    name: "stock",
  });
  const availableStock = calculateAvailableStockFromFormInput({
    stock,
    incomingStock: 0,
    committedStock: existingData.committedStock,
  });

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <DialogContent className="flex flex-col gap-4">
        <div>
          <InputLabel>Available from</InputLabel>
          <InputContainer>
            <InputText disabled={true} value={"Now"} />
          </InputContainer>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <H5>Continuous back orders</H5>
            <H6>
              Let customers place orders even if the product is out of stock.
            </H6>
          </div>

          <Controller
            name="canBackorder"
            control={control}
            defaultValue={false}
            render={({ field: { onChange, value } }) => (
              <Switch
                checked={!!value}
                onChange={(checked) => onChange(checked)}
              />
            )}
          />
        </div>

        <div>
          <InputLabel>Total (on hand) stock</InputLabel>
          <InputContainer className={"w-full"}>
            <InputNumber
              {...register("stock", {
                required: true,
                valueAsNumber: true,
              })}
              required={true}
            />
          </InputContainer>
        </div>

        <div className="flex flex-col gap-1">
          <div>
            <InputLabel>*Incoming stock</InputLabel>
            <InputContainer className={"w-full"}>
              <InputNumber disabled={true} value={"0"} />
            </InputContainer>
          </div>
        </div>

        <StockStatusCard
          availableStock={availableStock}
          committedStock={existingData.committedStock}
          canBackorder={canBackorder}
        />

        {remoteFeatureFlag.isActive("preOrders") && (
          <div>
            <InputLabel>Maximum order quantity</InputLabel>
            <InputContainer className={"w-full"}>
              <InputNumber
                {...register("maxOrderQuantity", {
                  valueAsNumber: true,
                })}
                min={1}
              />
            </InputContainer>
          </div>
        )}
      </DialogContent>

      <DialogCTA className="sticky bottom-0 bg-white">
        <div className="flex justify-end gap-2">
          {shouldTransferIncomingStockToOnHand && (
            <IncomingToOnHandStockTransferConfirmation
              confirmed={confirmed}
              onConfirmedChanged={setConfirmed}
            />
          )}
          <Button
            disabled={
              loading || (shouldTransferIncomingStockToOnHand && !confirmed)
            }
            type="submit"
          >
            Update
          </Button>
        </div>
      </DialogCTA>
    </form>
  );
};

const FutureReleaseInputForm = ({
  existingData,
  onFormSubmit,
  loading,
}: {
  existingData: ProductFormInputType;
  onFormSubmit: (data: ProductFormInputType) => void;
  loading: boolean;
}) => {
  const { register, handleSubmit, control, setValue } = useForm<
    Omit<ProductFormInputType, "availableFrom"> & { availableFrom: string }
  >({
    defaultValues: {
      incomingStock: existingData.incomingStock,
      stock: existingData.stock,
      availableFrom: dayjs(existingData.availableFrom).format("YYYY-MM-DD"),
      maxOrderQuantity: existingData.maxOrderQuantity,
      canBackorder: existingData.canBackorder,
    },
    shouldUseNativeValidation: true,
  });

  const canBackorder = useWatch({
    control,
    name: "canBackorder",
  });
  const availableFrom = useWatch({
    control,
    name: "availableFrom",
  });
  const stock = useWatch({
    control,
    name: "stock",
  });
  const incomingStock = useWatch({
    control,
    name: "incomingStock",
  });

  const availableStock = calculateAvailableStockFromFormInput({
    stock,
    incomingStock,
    committedStock: existingData.committedStock,
  });

  useEffect(() => {
    if (canBackorder) {
      setValue("incomingStock", 0);
    }
  }, [canBackorder, setValue]);

  return (
    <form
      onSubmit={handleSubmit((data) => {
        onFormSubmit({
          ...data,
          availableFrom: dayjs(data.availableFrom).toDate(),
        });
      })}
    >
      <DialogContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div>
            <InputLabel>Available from</InputLabel>
            <H6>
              Select the date when deliveries for this product will begin.
            </H6>
            <InputContainer>
              <InputDate
                min={dayjs().add(1, "day").startOf("day").format("YYYY-MM-DD")}
                {...register("availableFrom", {
                  valueAsDate: true,
                })}
                required={true}
              />
            </InputContainer>
          </div>

          <div className={"flex items-center gap-1 text-xs"}>
            <Icon
              icon={"INFORMATION_OUTLINE"}
              size={0.6}
              className="shrink-0"
            />
            <div className="text-sm text-zinc-500">
              Customers can pre-order this product. Their estimate delivery date
              will be calculated from the date you select, based on your
              delivery schedules.
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <H5>Continuous back orders</H5>
            <H6>
              Let customers place orders even if the product is out of stock.
            </H6>
          </div>

          <Controller
            name="canBackorder"
            control={control}
            defaultValue={false}
            render={({ field: { onChange, value } }) => (
              <Switch
                checked={!!value}
                onChange={(checked) => onChange(checked)}
              />
            )}
          />
        </div>

        <div>
          <InputLabel>Total (on hand) stock</InputLabel>
          <InputContainer className={"w-full"}>
            <InputNumber
              {...register("stock", {
                required: true,
                valueAsNumber: true,
              })}
              required={true}
            />
          </InputContainer>
        </div>

        <div className="flex flex-col gap-1">
          <div>
            <InputLabel>*Incoming stock</InputLabel>
            <InputContainer className={"w-full"}>
              <InputNumber
                {...register("incomingStock", {
                  required: true,
                  valueAsNumber: true,
                })}
                disabled={canBackorder}
                min={0}
              />
            </InputContainer>
          </div>
          {availableFrom && dayjs(availableFrom).isValid() && (
            <div className={"flex items-center gap-1 text-xs"}>
              <Icon icon={"INFORMATION_OUTLINE"} size={0.6} />
              <div className="text-sm text-zinc-500">
                Let customers order from a limited quantity of stock that will
                be available to deliver from{" "}
                {dayjs(availableFrom).format("DD/MM/YYYY")}.
              </div>
            </div>
          )}
        </div>

        <StockStatusCard
          availableStock={availableStock}
          committedStock={existingData.committedStock}
          canBackorder={canBackorder}
        />

        <div>
          <InputLabel>Maximum order quantity</InputLabel>
          <InputContainer className={"w-full"}>
            <InputNumber
              {...register("maxOrderQuantity", {
                valueAsNumber: true,
              })}
              min={1}
            />
          </InputContainer>
        </div>
      </DialogContent>

      <DialogCTA className="sticky bottom-0 bg-white">
        <div className="flex justify-end gap-2">
          <Button disabled={loading} type="submit">
            Update
          </Button>
        </div>
      </DialogCTA>
    </form>
  );
};

const ItemReleaseRadioSelect = ({
  itemRelease,
  onItemReleaseChange,
}: {
  itemRelease: "preOrder" | "generalRelease";
  onItemReleaseChange: (itemRelease: "preOrder" | "generalRelease") => void;
}) => {
  return (
    <RadioGroup
      value={itemRelease}
      onChange={(value) =>
        onItemReleaseChange(value as "preOrder" | "generalRelease")
      }
    >
      <RadioGroupContainer>
        <RadioGroupOption value={"generalRelease"} className={"p-4"}>
          <div className="flex flex-col">
            <H5>General release</H5>
            <p>Deliveries for this product can start today.</p>
          </div>
        </RadioGroupOption>

        <RadioGroupOption value={"preOrder"} className={"p-4"}>
          <div className="flex basis-full items-center justify-between">
            <div className="flex flex-col justify-start">
              <H5>Future release</H5>
              <p>Deliveries for this product can start on a future date.</p>
            </div>
            <NewTag />
          </div>
        </RadioGroupOption>
      </RadioGroupContainer>
    </RadioGroup>
  );
};

export default function StockManagementDetailsDialog({
  active,
  onDismiss,
  afterLeave,
  product,
}: {
  active: boolean;
  product?: ProductFormInputType & { id: number };
  onDismiss: () => void;
  afterLeave?: () => void;
}) {
  return (
    <Dialog
      active={active && product !== undefined}
      onDismiss={onDismiss}
      afterLeave={afterLeave}
    >
      <DialogNavigation
        title="Stock management"
        description="Define how you want to manage your stock"
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
      />
      {product && (
        <StockManagementContent
          key={product.id}
          product={product}
          onDismiss={onDismiss}
        />
      )}
    </Dialog>
  );
}

const StockManagementContent = ({
  product,
  onDismiss,
}: {
  product: ProductFormInputType & { id: number };
  onDismiss: () => void;
}) => {
  const remoteFeatureFlag = useRemoteFeatureFlags();
  const skuIsFutureRelease =
    !!product.availableFrom && dayjs().isBefore(product.availableFrom);
  const [itemRelease, setItemRelease] = useState<"preOrder" | "generalRelease">(
    skuIsFutureRelease ? "preOrder" : "generalRelease",
  );
  const productUpdateMutation = useProductUpdate(product.id);

  const onUpdateProduct = (details: ProductFormInputType) => {
    productUpdateMutation.mutate(details, { onSuccess: () => onDismiss() });
  };

  return (
    <>
      <DialogContent>
        {productUpdateMutation.isError && (
          <Alert
            type="error"
            message={
              apiV2AssertError(productUpdateMutation.error)?.response?.data
                .message ?? "Unable to save changes. Please try again."
            }
          />
        )}
        {remoteFeatureFlag.isActive("preOrders") && (
          <div className="my-6">
            <PreorderMarketingCard />
          </div>
        )}
        {(remoteFeatureFlag.isActive("preOrders") || skuIsFutureRelease) && (
          <ItemReleaseRadioSelect
            itemRelease={itemRelease}
            onItemReleaseChange={(itemRelease) => setItemRelease(itemRelease)}
          />
        )}
      </DialogContent>

      {itemRelease === "generalRelease" ? (
        <GeneralReleaseInputForm
          loading={productUpdateMutation.isPending}
          onFormSubmit={onUpdateProduct}
          existingData={product}
        />
      ) : (
        <FutureReleaseInputForm
          loading={productUpdateMutation.isPending}
          onFormSubmit={onUpdateProduct}
          existingData={product}
        />
      )}
    </>
  );
};
